/* eslint-disable import/no-named-as-default */
import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import { Helmet } from "react-helmet-async";
import { Redirect } from "react-router-dom";

import Menu from "./Menu";
import CatalogFooter from "./Footer-new";
import { GoogleOAuthProvider, googleLogout } from "@react-oauth/google";
import CookieConsent from "react-cookie-consent";
import { Typography } from "@material-ui/core";
import { useAdminWebSocketStore, adminWsManager } from "../../stores/websocketStore";
import utils from "../../universal/utils";
import { canViewDashboard } from "../../universal/adminRoles";
// import RTL from "./RTL";

const basePath = utils.getEdgeBasePath();

function clearLegacyClientAuth() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem("ouroAuthToken");
    window.localStorage.removeItem("ouroGoogleToken");
  } catch (error) {
    // Best-effort cleanup only.
  }

  if (typeof document !== "undefined") {
    document.cookie = "ouro-jwt=; Max-Age=0; path=/";
  }
}

const styles = (theme) => ({
  spacer: {
    ...theme.mixins.toolbar,
  },
});

function App({ classes, catalog, actions, dispatch, children }) {
  const [state, setState] = React.useState({
    isAuthenticated: false,
    user: null,
    showRefreshWindow: false,
    autoLoginInProgress: true,
    googleAuthFailure: null,
  });

  const { connect, disconnect, send, subscribe } = useAdminWebSocketStore();

  React.useEffect(() => {
    // Provide Redux dispatch to WebSocket manager so it can push updates
    if (dispatch) {
      adminWsManager.setDispatch(dispatch);
    }
  }, [dispatch]);

  const onFailure = React.useCallback(() => {
    clearLegacyClientAuth();
  }, []);

  const restoreSession = React.useCallback(
    () => {
      setState((prev) => ({
        ...prev,
        autoLoginInProgress: true,
        googleAuthFailure: null,
      }));

      fetch(`${basePath}/auth/session/admins`, {
        method: "GET",
        credentials: "include",
        mode: "cors",
        cache: "default",
      })
        .then(async (r) => {
          let resp = {};
          try {
            resp = await r.json();
          } catch (e) {
            // ignore parse errors; handle by status/token checks below
          }

          if (!r.ok) {
            const msg = resp?.message || `Admin login failed (HTTP ${r.status})`;
            throw new Error(msg);
          }

          const user = resp?.user;
          if (!user) {
            const msg = resp?.message || "Admin login failed";
            throw new Error(msg);
          }

          actions.setUser({ user });
          setState((prev) => ({
            ...prev,
            isAuthenticated: true,
            user,
            autoLoginInProgress: false,
            googleAuthFailure: null,
          }));
        })
        .catch((err) => {
          onFailure();
          setState((prev) => ({
            ...prev,
            isAuthenticated: false,
            user: null,
            autoLoginInProgress: false,
            googleAuthFailure: err?.message || "Admin login failed",
          }));
        });
    },
    [actions, onFailure]
  );

  const googleResponse = ({ credential }) => {
    const options = {
      method: "POST",
      body: JSON.stringify({ credential }),
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      mode: "cors",
      cache: "default",
    };

    setState((prev) => ({
      ...prev,
      autoLoginInProgress: true,
      googleAuthFailure: null,
    }));
    fetch(`${basePath}/auth/google/admins`, options)
      .then(async (r) => {
        let resp = {};
        try {
          resp = await r.json();
        } catch (e) {
          // ignore parse errors; handle by status/token checks below
        }

        if (!r.ok) {
          const msg = resp?.message || `Admin login failed (HTTP ${r.status})`;
          throw new Error(msg);
        }

        const user = resp?.user;
        if (!user) {
          const msg = resp?.message || "Admin login failed";
          throw new Error(msg);
        }

        actions.setUser({ user });
        setState((prev) => ({
          ...prev,
          isAuthenticated: true,
          user,
          autoLoginInProgress: false,
          googleAuthFailure: null,
        }));
      })
      .catch((err) => {
        onFailure();
        setState((prev) => ({
          ...prev,
          isAuthenticated: false,
          user: null,
          autoLoginInProgress: false,
          googleAuthFailure: err?.message || "Admin login failed",
        }));
      });
  };

  React.useEffect(() => {
    clearLegacyClientAuth();
    restoreSession();
  }, [restoreSession]);

  // Connect WebSocket when user is authenticated
  React.useEffect(() => {
    if (state.isAuthenticated && state.user && canViewDashboard(state.user)) {
      connect(state.user);
    } else {
      disconnect();
    }
  }, [state.isAuthenticated, state.user, connect, disconnect]);

  const logout = () => {
    // Send logout message via WebSocket before disconnecting
    send({
      messageId: "user-logout"
    });
    
    setState((prev) => ({
      ...prev,
      isAuthenticated: false,
      user: null,
      autoLoginInProgress: false,
      googleAuthFailure: null,
    }));
    fetch(`${basePath}/auth/logout/admins`, {
      method: "POST",
      credentials: "include",
      mode: "cors",
      cache: "no-cache",
    }).catch(() => {});
    clearLegacyClientAuth();
    googleLogout();
    actions.logout();
  };

  const { isAuthenticated, user, autoLoginInProgress } = state;
  const { performLogout } = catalog;

  const baseDescription =
    "Ouro is a new alternative matchmaking service for CS2";

  return (
    // <RTL>
    <div>
      <GoogleOAuthProvider clientId="1068129342176-704fo06p1eqtqp9me1o948btj290qsf5.apps.googleusercontent.com">
        <Menu
          actions={actions}
          catalog={catalog}
          logout={logout}
          autoLoginInProgress={autoLoginInProgress}
          isAuthenticated={isAuthenticated}
          googleResponse={googleResponse}
          user={user}
          onFailure={onFailure}
        />
        <Helmet>
          <meta charSet="utf-8" />
          <meta name="Content-language" content="en" />
          <meta
            property="og:title"
            content="Ouro: The Competitive Cycle Begins Here - Coming soon!"
          />
          {/* <meta
          property="og:image"
          content="https://s3.eu-west-2.amazonaws.com/sportyshop/catalog/homepage/covers/cover.jpeg"
        /> */}
          <meta property="og:site_name" content="Ouro matchmaking for cs2" />
          <meta property="og:description" content={baseDescription} />
          <meta property="description" content={baseDescription} />

          <title>
            Ouro: The Competitive Cycle Begins Here - Coming soon!
          </title>
        </Helmet>

        <div className={classes.spacer}></div>

        {performLogout === true && <Redirect to={`/`} />}
        {!autoLoginInProgress && !isAuthenticated && <Redirect to={`/`} />}

        {children}
        <CatalogFooter />
        <CookieConsent
          location="bottom"
          buttonText={"ACCEPT"}
          cookieName="ouro-matchmaking-consent"
          style={{ background: "#2B373B" }}
          buttonStyle={{
            color: "#fefefe",
            padding: 8,
            position: "relative",
            top: 10,
            right: 5,
            fontWeight: 525,
            background: "#ff8f05",
            fontSize: "11pt",
            borderRadius: 5,
          }}
        >
          <Typography>
            {
              "We use cookies to enhance your browsing experience and analyze site traffic."
            }{" "}
            <br />
            {
              'By clicking "Accept" you consent to the use of cookies in accordance with our Cookie Policy. For more information, please read our Privacy Policy.'
            }
          </Typography>
        </CookieConsent>
      </GoogleOAuthProvider>
    </div>
    // </RTL>
  );
}

App.propTypes = {
  children: PropTypes.node,
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
  dispatch: PropTypes.func,
};

export default withStyles(styles)(App);
